import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';

import { useAccountRole } from 'src/hooks/auth/use-account-role';

import { AiRestrictions } from 'src/services/ai/ai.types';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { useGetAiRestrictionsQuery, usePutAiRestrictionsMutation } from 'src/services/ai/ai.service';

/**
 * AI Restrictions (JT1-1751): what the AI may read about the agency's candidates. Admins only,
 * matching the API. Saved through PUT /ai/restrictions (JT1-1750).
 */

/** The candidate document types the backend accepts (`CANDIDATE_DOCUMENT_TYPES`). */
export const DOCUMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'resume', label: 'Résumé' },
  { value: 'cover_letter', label: 'Cover letter' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other_license', label: 'Other licence' },
  { value: 'visa', label: 'Visa' },
  { value: 'driving_license', label: "Driver's licence" },
  { value: 'passport', label: 'Passport' },
  { value: 'tax_document', label: 'Tax document' },
  { value: 'insurance_document', label: 'Insurance document' },
  { value: 'other', label: 'Other' },
];

/** Profile sections the backend accepts, plus contact details, which it treats as a section. */
export const PROFILE_SECTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'executive_summary', label: 'Summary' },
  { value: 'work_experiences', label: 'Work history' },
  { value: 'skills', label: 'Skills' },
  { value: 'education_history', label: 'Education history' },
  { value: 'education_level', label: 'Education level' },
  { value: 'job_types', label: 'Job types' },
  { value: 'working_styles', label: 'Work styles' },
  { value: 'work_locations', label: 'Work locations' },
  { value: 'work_rights', label: 'Work rights' },
  { value: 'salary_expectations', label: 'Salary expectations' },
  { value: 'notice_period', label: 'Notice period' },
  { value: 'links', label: 'Links' },
  { value: 'contact_details', label: 'Contact details (email and phone)' },
];

const labelOf = (options: { value: string; label: string }[]) => (value: string) =>
  options.find((option) => option.value === value)?.label ?? value;

const SWITCHES: { key: 'allow_notes' | 'allow_emails' | 'allow_transcripts'; label: string; help: string }[] = [
  { key: 'allow_notes', label: 'Recruiter notes', help: 'Notes your team writes on a candidate.' },
  { key: 'allow_emails', label: 'Emails', help: 'Email threads with the candidate. These include what other people wrote.' },
  { key: 'allow_transcripts', label: 'Call and meeting transcripts', help: 'Meeting summaries and call transcripts.' },
];

export default function AiRestrictionsCard() {
  const role = useAccountRole();
  const isAdmin = role === AccountRole.Admin || role === AccountRole.Op_Manager;

  const { data, isLoading, isError } = useGetAiRestrictionsQuery(undefined, { skip: !isAdmin });
  const [putAiRestrictions, { isLoading: isSaving }] = usePutAiRestrictionsMutation();

  const [values, setValues] = useState<AiRestrictions | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  if (!isAdmin) return null;

  const handleSave = async () => {
    if (!values) return;
    setSaveError(false);
    try {
      const saved = await putAiRestrictions(values).unwrap();
      setSavedVersion(saved.policy_version ?? null);
    } catch {
      setSaveError(true);
    }
  };

  const changed = !!values && !!data && JSON.stringify(values) !== JSON.stringify(data);
  const update = (patch: Partial<AiRestrictions>) => {
    setValues((current) => (current ? { ...current, ...patch } : current));
    setSavedVersion(null);
  };

  return (
    <Card>
      <CardHeader
        title="AI Restrictions"
        subheader="Choose what the AI may read about your candidates."
      />
      <CardContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isError && <Alert severity="error">Could not load the AI restrictions. Please try again.</Alert>}

        {values && (
          <Stack gap={3}>
            <Alert severity="info">
              These settings cover AI matching, ranking, candidate sourcing and the AI assistant.{' '}
              <strong>Résumé parsing when a résumé is uploaded is not affected</strong>, and nothing your team can see
              in JobTetris changes.
            </Alert>

            <Stack gap={1}>
              <Typography variant="subtitle2">Notes and conversations</Typography>
              {SWITCHES.map(({ key, label, help }) => (
                <FormControlLabel
                  key={key}
                  control={<Switch checked={values[key]} onChange={(event) => update({ [key]: event.target.checked })} />}
                  label={
                    <Stack>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{help}</Typography>
                    </Stack>
                  }
                />
              ))}
            </Stack>

            <Divider />

            <Stack gap={1.5}>
              <FormControlLabel
                control={<Switch checked={values.allow_documents} onChange={(event) => update({ allow_documents: event.target.checked })} />}
                label={<Typography variant="subtitle2">Documents</Typography>}
              />
              <Autocomplete
                multiple
                disabled={!values.allow_documents}
                options={DOCUMENT_TYPE_OPTIONS.map((option) => option.value)}
                getOptionLabel={labelOf(DOCUMENT_TYPE_OPTIONS)}
                value={values.allowed_document_types}
                onChange={(_event, value) => update({ allowed_document_types: value })}
                renderInput={(params) => <TextField {...params} label="Document types the AI may read" />}
              />
            </Stack>

            <Divider />

            <Autocomplete
              multiple
              options={PROFILE_SECTION_OPTIONS.map((option) => option.value)}
              getOptionLabel={labelOf(PROFILE_SECTION_OPTIONS)}
              value={values.allowed_profile_sections}
              onChange={(_event, value) => update({ allowed_profile_sections: value })}
              renderInput={(params) => <TextField {...params} label="Profile sections the AI may read" />}
            />

            {savedVersion !== null && (
              <Alert severity="success">
                Saved. The change is being applied across all your candidates, which takes a few minutes for most
                agencies and up to an hour for very large databases. Until it finishes, AI search only uses
                candidates already updated, so results may look thinner for a while.
              </Alert>
            )}
            {saveError && <Alert severity="error">The restrictions could not be saved. Please try again.</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || !changed}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Save Restrictions
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
